import { DateTime } from './../DateTime';
import {
  flow,
  types,
  ModelPropertiesDeclaration,
  getEnv,
  SnapshotIn,
  IMapType,
  IAnyType,
  IModelType,
  ISimpleType,
  Instance,
  SnapshotOut,
  applySnapshot,
  IReferenceType,
  IMaybe,
  IAnyModelType,
  destroy,
  getRoot,
  getType,
  getSnapshot,
} from 'mobx-state-tree';

import stringHash from 'string-hash';

import { IRootEnv, RootModelType } from '../Root';
import { when } from 'mobx';

export interface IPaginationResponse<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
  sums?: any;
}

export interface IPaginationFilter<IT extends { id: number | string }> {
  filter?: Partial<{ [K in keyof IT]: string | number }> & {
    search?: string | number;
  } & any;
  ordering?: string;
}

export interface IWithCRUDProps<IT extends IAnyType>
  extends ModelPropertiesDeclaration {
  itemMap: IMapType<IT>;
}

export type GenericModelWithCRUDType = IModelType<
  {
    id: ISimpleType<number | string>;
  },
  any,
  any,
  any
>;

export interface ICacheResults<S> {
  items: Instance<S>[];
  total: number;
  hasMore: boolean;
}

export interface IQueryCache<S> {
  items: Instance<S>[];
  total: number;
  hasMore: boolean;
  offset: number;
  isLoading: boolean;
  sums?: any;
}

export function modelWithCRUD<S extends GenericModelWithCRUDType>(
  itemName: string,
  ItemType: S,
  organisationalPermissions = true
) {
  const QueryCache = types
    .model({
      hash: types.identifier,
      query: types.string,

      sums: types.maybe(types.frozen()),

      isLoading: types.optional(types.boolean, false),
      error: types.optional(types.boolean, false),
      offset: types.optional(types.number, 0),
      total: types.optional(types.number, Infinity),
      lastFetch: types.optional(DateTime, () => new Date()),

      items: types.array(
        types.late(
          (): IMaybe<IReferenceType<IAnyModelType>> =>
            types.safeReference(ItemType)
        )
      ),
    })
    .views((self) => ({
      get results(): ICacheResults<S> {
        // TODO: Maybe we can remove the filter? This will reduce the complexity to O(1)
        const res = {
          items: self.items as Instance<S>,
          total: self.total,
          hasMore: self.items.length < (self.total || 0),
        };
        return res;
      },
      get isHungry() {
        return self.offset < (self.total || 0);
      },
      get hasMore() {
        return self.items.length < (self.total || 0);
      },
    }))
    .actions((self) => ({
      invalidate() {
        self.items.replace([]);
        self.offset = 0;
        self.total = Infinity;
        self.isLoading = false;
        self.error = false;
      },
    }));

  const model = types
    .model({
      itemMap: types.map(ItemType),
      queryCache: types.map(QueryCache),

      isLoading: types.optional(types.boolean, false),
      requestQueue: types.array(types.string),
    })
    .views((self) => ({
      get env() {
        return getEnv<IRootEnv>(self);
      },
      get items(): Instance<typeof ItemType>[] {
        return Array.from(self.itemMap.values());
      },
      get size() {
        return self.itemMap.size;
      },
      get root() {
        return getRoot(self) as RootModelType;
      },
      get apiUrl(): string {
        return this.env.api.defaults.baseURL || '';
      },
      get apiPath(): string {
        if (!organisationalPermissions) {
          return `/${itemName}`;
        }
        const root = this.root;
        if (!root.authStore.currentOrganisation) {
          throw new Error(
            `AuthStore not ready yet. Wait to use any modelWithCrud stores until the AuthStore has a currentOrganisation. Check ${
              getType(self).name
            }`
          );
        }
        return `/organisations/${
          root.authStore.currentOrganisation!.id
        }/${itemName}`;
      },
    }))
    .actions((self) => ({
      putItem(item: S) {
        return self.itemMap.put(item);
      },
      _spliceQueue(index: number, end: number) {
        return self.requestQueue.splice(index, end);
      },
    }))
    .actions((self) => ({
      _dequeue: flow(function* dequeue() {
        if (self.requestQueue.length === 0) return;

        self.isLoading = true;

        // Remember, we can ONLY READ inside this function.
        const fetch = async () => {
          // Dequeue
          const queue = self._spliceQueue(0, self.env.limit);

          // Now get all from server
          let data: IPaginationResponse<SnapshotIn<S>> = await self.env.api
            .get(
              `${self.apiPath}/?limit=${self.env.limit}&id__in=${queue.join(
                ','
              )}`
            )
            .then((res: { data: any }) => res.data);

          // Remember to assign data
          data.results.forEach((item: SnapshotIn<S>) => self.putItem(item));
          if (self.requestQueue.length > 0) {
            await fetch();
          }
        };
        yield fetch();
        self.isLoading = false;
      }),
    }))
    .actions((self) => {
      let timeoutID: any | undefined = undefined;

      return {
        fetchMore: flow<Instance<typeof QueryCache>, any>(function* fetch(
          filter: IPaginationFilter<SnapshotOut<S>> | string = {},
          limit?: number
        ) {
          // Build filter
          let filterQuery = '';
          if (typeof filter === 'string') {
            filterQuery = filter;
          } else {
            let filtering = { ...filter.filter };
            filterQuery = `${
              filter.ordering ? `ordering=${filter.ordering}&` : ''
            }${Object.keys(filtering || {})
              .filter(
                (k) => filtering[k] !== undefined && filtering[k] !== null
              )
              .map((key) => `${key}=${filtering![key]}`)
              .filter((f) => !!f)
              .join('&')}`;
          }

          const hash = stringHash(filterQuery) + '';
          const cache = self.queryCache.has(hash)
            ? self.queryCache.get(hash)!
            : self.queryCache.put({
                hash,
                query: filterQuery,
              });

          // If loading, we should just return - and let the requester request more.
          if (cache.isLoading) {
            yield when(() => !cache.isLoading);
            if (!cache.isHungry) return cache;
          }
          // If we have already fetched from offset 0 to TOTAL - we simply return the results
          if (!cache.isHungry) return cache;
          cache.isLoading = true;
          const query = `?offset=${cache.offset}&limit=${
            limit || self.env.limit
          }${filterQuery ? `&${filterQuery}` : ''}`;

          cache.offset = cache.offset + (limit || self.env.limit);

          try {
            self.isLoading = true;
            const data: IPaginationResponse<SnapshotIn<S>> = yield self.env.api
              .get(`${self.apiPath}/${query}`)
              .then((res: { data: any }) => res.data);
            self.isLoading = false;

            // build response into items and put to our itemMap
            data.results.forEach((item: SnapshotIn<S>) => {
              const old = self.itemMap.get(item.id);
              self.itemMap.put({ ...(old ? getSnapshot(old) : {}), ...item });
            });

            // Compute new set of identifiers to cache
            const newSet = new Set([
              ...getSnapshot(cache).items,
              ...data.results.map((d: SnapshotIn<S>) => d.id),
            ]);

            // Build new cache
            applySnapshot(cache, {
              hash,
              query: filterQuery,
              items: Array.from(newSet),
              offset: cache.offset,
              total: data.count,
              isLoading: false,
              sums: data.sums,
            });
          } catch (err) {
            //cache.isLoading = false;
            cache.error = true;
            self.isLoading = false;
            throw err;
          }
          // Return the result list
          return cache;
        }),
        getItem: flow<Instance<S>, any>(function* getItem(
          id: SnapshotIn<S>['id'],
          forceFetch: boolean | undefined = false
        ) {
          let item = self.itemMap.get(id + '');
          if (item && !forceFetch) return item;
          let data = yield self.env.api
            .get(`${self.apiPath}/${id}/`)
            .then((res: { data: any }) => res.data);
          item = self.itemMap.put(data);
          return item as Instance<S>;
        }),
        getItemWithQueue(id: SnapshotIn<S>['id'], force?: boolean) {
          let item = self.itemMap.get(id + '');
          if (item && !force) return;

          // eslint-disable-next-line eqeqeq
          if (self.requestQueue.findIndex((i) => i == id) > -1) return;

          self.requestQueue.push(id + '');

          if (!timeoutID) {
            timeoutID = setTimeout(() => {
              self._dequeue();
              timeoutID = undefined;
            }, 20);
          }
        },
      };
    })
    .actions((self) => ({
      /**
       * Gets all items in this store using a filter - uses caches.
       */
      getAll: flow(function* getAll(
        filter: IPaginationFilter<SnapshotOut<S>> | string = {},
        offset: number = Infinity
      ) {
        const recurse = async (): Promise<Instance<typeof QueryCache>> => {
          const cache = await self.fetchMore(filter);
          if (cache.hasMore && cache.offset < offset) {
            return recurse();
          }
          return cache;
        };
        const cache = yield recurse();
        return cache as IQueryCache<any>;
      }),
    }))
    .actions((self) => ({
      invalidateCaches() {
        self.queryCache.forEach((cache) => {
          const toRefill = Math.max(cache.offset, self.env.limit * 3); // We will ONLY refill 3 times our limit to preserve some bandwidth
          cache.invalidate();
          self.getAll(cache.query, toRefill);
        });
      },
    }))
    .views((self) => ({
      getItemNaive(id: SnapshotIn<S>['id']) {
        const item = self.itemMap.get(id + '') || undefined;
        if (!item) {
          setImmediate(() => self.getItemWithQueue(id));
        }
        return item;
      },
    }))
    .actions((self) => ({
      getItemNaiveAsync: flow(function* getItemNaiveAsync(
        id: SnapshotIn<S>['id']
      ) {
        if (!id) {
          throw new TypeError('id cannot be empty!');
        }
        const item = self.getItemNaive(id);
        if (item) return item;
        yield when(() => !!self.itemMap.get(id));
        return self.itemMap.get(id);
      }),
      /**
       * Creating an item invalidates all caches maintained by this store
       */
      createItem: flow(function* createItem(
        item: Partial<SnapshotIn<S>>
      ): Generator<Promise<S>, any, any> {
        const data = yield self.env.api
          .post(`${self.apiPath}/`, item)
          .then((res: { data: any }) => res.data);
        const created = self.itemMap.put(data);
        self.invalidateCaches();
        return created;
      }),
      updateItem: flow(function* updateItem(
        item: Instance<S>,
        changes: Partial<SnapshotIn<S>>
      ): Generator<Promise<S>, any, any> {
        const data = yield self.env.api
          .put(`${self.apiPath}/${item.id}/`, changes)
          .then((res) => res.data);
        self.invalidateCaches();
        return yield self.getItem(data.id, true)!;
      }),
      /**
       * Deleting an item invalidates all caches maintained by this store
       */
      deleteItem: flow(function* deleteItem(item: Instance<S>) {
        yield self.env.api.delete(`${self.apiPath}/${item.id}/`);
        destroy(item);
        self.invalidateCaches();
      }),
    }));

  return model;
}
